'use client';

export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export const TRACKING_PARAM_KEYS = [
  ...UTM_PARAM_KEYS,
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'yclid',
  'mc_cid',
  'mc_eid',
  'igshid',
  'si',
  'ref',
  'ref_src',
  'ref_url',
  'source',
  'spm',
] as const;

export type UtmParamKey = (typeof UTM_PARAM_KEYS)[number];
export type UrlTransformMode = 'full-url' | 'query-string' | 'query-value' | 'path-segment';

export type UrlQueryParam = {
  index: number;
  key: string;
  value: string;
  hasValue: boolean;
  isRepeated: boolean;
  isUtm: boolean;
  isTracking: boolean;
};

export type ParsedUrlAnalysis = {
  normalizedUrl: string;
  warning: string | null;
  origin: string;
  path: string;
  fragment: string;
  baseUrl: string;
  orderedParams: UrlQueryParam[];
  duplicateKeys: string[];
  emptyValueKeys: string[];
  bareKeys: string[];
  utmParams: Partial<Record<UtmParamKey, string[]>>;
  extraParams: UrlQueryParam[];
  trackingParams: UrlQueryParam[];
};

export type UrlDifference = {
  key: string;
  left: string[];
  right: string[];
};

export type UrlComparisonResult = {
  originChanged: boolean;
  pathChanged: boolean;
  fragmentChanged: boolean;
  addedParams: Array<{ key: string; values: string[] }>;
  removedParams: Array<{ key: string; values: string[] }>;
  changedParams: UrlDifference[];
  repeatedKeyDifferences: string[];
};

export type CampaignUrlInspection = {
  destinationUrl: string;
  statusLabels: string[];
  missingRequiredUtms: UtmParamKey[];
  duplicateUtmKeys: string[];
  extraTrackingParams: UrlQueryParam[];
  hasAnyUtm: boolean;
};

type DecodedComponent = {
  value: string;
  warning: string | null;
};

type NormalizedUrl = {
  normalized: string;
  url: URL;
  warning: string | null;
};

const trackingKeySet = new Set<string>(TRACKING_PARAM_KEYS);
const utmKeySet = new Set<string>(UTM_PARAM_KEYS);

function safeDecodeComponent(value: string, plusAsSpace = false): DecodedComponent {
  const normalized = plusAsSpace ? value.replace(/\+/g, '%20') : value;

  try {
    return { value: decodeURIComponent(normalized), warning: null };
  } catch {
    return {
      value,
      warning: 'Some encoded characters are invalid and were left unchanged.',
    };
  }
}

function normalizeUrlInput(value: string): NormalizedUrl {
  const trimmed = value.trim();

  if (!trimmed) {
    throw new Error('Enter one full URL first.');
  }

  let normalized = trimmed;
  let warning: string | null = null;

  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    normalized = trimmed.startsWith('//') ? `https:${trimmed}` : `https://${trimmed}`;
    warning = 'Added https:// so the URL could be parsed.';
  }

  let url: URL;
  try {
    url = new URL(normalized);
  } catch {
    throw new Error('Enter a valid full URL like https://example.com/page?utm_source=google.');
  }

  if (!/^https?:$/i.test(url.protocol)) {
    throw new Error('Use an http or https URL for this tool.');
  }

  return { normalized: url.toString(), url, warning };
}

function serializeQueryEntries(entries: UrlQueryParam[]) {
  return entries
    .map((entry) =>
      entry.hasValue
        ? `${encodeURIComponent(entry.key)}=${encodeURIComponent(entry.value)}`
        : encodeURIComponent(entry.key),
    )
    .join('&');
}

function groupParamValues(entries: UrlQueryParam[]) {
  const grouped = new Map<string, string[]>();

  for (const entry of entries) {
    const values = grouped.get(entry.key) ?? [];
    values.push(entry.value);
    grouped.set(entry.key, values);
  }

  return grouped;
}

export function parseUrlAnalysis(value: string): ParsedUrlAnalysis {
  const { normalized, url, warning } = normalizeUrlInput(value);
  const queryString = url.search.length > 1 ? url.search.slice(1) : '';
  const rawParts = queryString ? queryString.split('&').filter((part) => part.length > 0) : [];
  const decodeWarnings = new Set<string>();
  const orderedParams = rawParts.map((part, index): UrlQueryParam => {
    const separatorIndex = part.indexOf('=');
    const hasValue = separatorIndex >= 0;
    const rawKey = hasValue ? part.slice(0, separatorIndex) : part;
    const rawValue = hasValue ? part.slice(separatorIndex + 1) : '';
    const decodedKey = safeDecodeComponent(rawKey, true);
    const decodedValue = safeDecodeComponent(rawValue, true);

    if (decodedKey.warning) {
      decodeWarnings.add(decodedKey.warning);
    }

    if (decodedValue.warning) {
      decodeWarnings.add(decodedValue.warning);
    }

    return {
      index,
      key: decodedKey.value,
      value: hasValue ? decodedValue.value : '',
      hasValue,
      isRepeated: false,
      isUtm: utmKeySet.has(decodedKey.value),
      isTracking: trackingKeySet.has(decodedKey.value),
    };
  });

  const keyCounts = new Map<string, number>();
  for (const entry of orderedParams) {
    keyCounts.set(entry.key, (keyCounts.get(entry.key) ?? 0) + 1);
  }

  for (const entry of orderedParams) {
    entry.isRepeated = (keyCounts.get(entry.key) ?? 0) > 1;
  }

  const duplicateKeys = [...keyCounts.entries()]
    .filter(([, count]) => count > 1)
    .map(([key]) => key);
  const emptyValueKeys = orderedParams
    .filter((entry) => entry.hasValue && entry.value.length === 0)
    .map((entry) => entry.key);
  const bareKeys = orderedParams.filter((entry) => !entry.hasValue).map((entry) => entry.key);
  const utmParams = Object.fromEntries(
    UTM_PARAM_KEYS.map((key) => [
      key,
      orderedParams.filter((entry) => entry.key === key).map((entry) => entry.value),
    ]),
  ) as Partial<Record<UtmParamKey, string[]>>;
  const extraParams = orderedParams.filter((entry) => !entry.isUtm);
  const trackingParams = orderedParams.filter((entry) => entry.isTracking);
  const combinedWarning = [warning, ...decodeWarnings].filter(Boolean).join(' ').trim() || null;

  return {
    normalizedUrl: normalized,
    warning: combinedWarning,
    origin: url.origin,
    path: url.pathname,
    fragment: url.hash ? safeDecodeComponent(url.hash.slice(1)).value : '',
    baseUrl: `${url.origin}${url.pathname}`,
    orderedParams,
    duplicateKeys,
    emptyValueKeys,
    bareKeys,
    utmParams,
    extraParams,
    trackingParams,
  };
}

export function removeParamsFromAnalysis(
  analysis: ParsedUrlAnalysis,
  keysToRemove: Iterable<string>,
): string {
  const removalSet = new Set([...keysToRemove]);
  const keptEntries = analysis.orderedParams.filter((entry) => !removalSet.has(entry.key));
  const queryString = serializeQueryEntries(keptEntries);
  const fragment = analysis.fragment ? `#${encodeURIComponent(analysis.fragment)}` : '';
  return `${analysis.baseUrl}${queryString ? `?${queryString}` : ''}${fragment}`;
}

export function cleanupUtmParams(analysis: ParsedUrlAnalysis) {
  return removeParamsFromAnalysis(analysis, UTM_PARAM_KEYS);
}

export function encodeTextByMode(value: string, mode: UrlTransformMode) {
  if (!value) {
    return '';
  }

  if (mode === 'query-value' || mode === 'path-segment') {
    return encodeURIComponent(value);
  }

  if (mode === 'query-string') {
    return value
      .split('&')
      .filter((segment) => segment.length > 0)
      .map((segment) => {
        const separatorIndex = segment.indexOf('=');
        const hasValue = separatorIndex >= 0;
        const key = hasValue ? segment.slice(0, separatorIndex) : segment;
        const rawValue = hasValue ? segment.slice(separatorIndex + 1) : '';
        return hasValue
          ? `${encodeURIComponent(key)}=${encodeURIComponent(rawValue)}`
          : encodeURIComponent(key);
      })
      .join('&');
  }

  return encodeURI(value);
}

export function decodeTextByMode(value: string, mode: UrlTransformMode): DecodedComponent {
  if (!value) {
    return { value: '', warning: null };
  }

  if (mode === 'query-value') {
    return safeDecodeComponent(value, true);
  }

  if (mode === 'path-segment') {
    return safeDecodeComponent(value, false);
  }

  if (mode === 'query-string') {
    const decodeWarnings = new Set<string>();
    const decoded = value
      .split('&')
      .filter((segment) => segment.length > 0)
      .map((segment) => {
        const separatorIndex = segment.indexOf('=');
        const hasValue = separatorIndex >= 0;
        const key = safeDecodeComponent(
          hasValue ? segment.slice(0, separatorIndex) : segment,
          true,
        );
        const nextValue = hasValue
          ? safeDecodeComponent(segment.slice(separatorIndex + 1), true)
          : { value: '', warning: null };

        if (key.warning) {
          decodeWarnings.add(key.warning);
        }

        if (nextValue.warning) {
          decodeWarnings.add(nextValue.warning);
        }

        return hasValue ? `${key.value}=${nextValue.value}` : key.value;
      })
      .join('&');

    return {
      value: decoded,
      warning: [...decodeWarnings].join(' ').trim() || null,
    };
  }

  try {
    return { value: decodeURI(value), warning: null };
  } catch {
    return {
      value,
      warning: 'Some encoded characters are invalid and were left unchanged.',
    };
  }
}

export function compareUrlAnalyses(
  left: ParsedUrlAnalysis,
  right: ParsedUrlAnalysis,
): UrlComparisonResult {
  const leftGrouped = groupParamValues(left.orderedParams);
  const rightGrouped = groupParamValues(right.orderedParams);
  const allKeys = [...new Set([...leftGrouped.keys(), ...rightGrouped.keys()])].sort();
  const addedParams: Array<{ key: string; values: string[] }> = [];
  const removedParams: Array<{ key: string; values: string[] }> = [];
  const changedParams: UrlDifference[] = [];
  const repeatedKeyDifferences: string[] = [];

  for (const key of allKeys) {
    const leftValues = leftGrouped.get(key);
    const rightValues = rightGrouped.get(key);

    if (!leftValues && rightValues) {
      addedParams.push({ key, values: rightValues });
      continue;
    }

    if (leftValues && !rightValues) {
      removedParams.push({ key, values: leftValues });
      continue;
    }

    if (!leftValues || !rightValues) {
      continue;
    }

    if (
      leftValues.length !== rightValues.length ||
      leftValues.some((value, index) => value !== rightValues[index])
    ) {
      changedParams.push({ key, left: leftValues, right: rightValues });
    }

    if (
      (leftValues.length > 1 || rightValues.length > 1) &&
      (leftValues.length !== rightValues.length ||
        leftValues.some((value, index) => value !== rightValues[index]))
    ) {
      repeatedKeyDifferences.push(key);
    }
  }

  return {
    originChanged: left.origin !== right.origin,
    pathChanged: left.path !== right.path,
    fragmentChanged: left.fragment !== right.fragment,
    addedParams,
    removedParams,
    changedParams,
    repeatedKeyDifferences,
  };
}

export function inspectCampaignUrl(analysis: ParsedUrlAnalysis): CampaignUrlInspection {
  const requiredKeys: UtmParamKey[] = ['utm_source', 'utm_medium', 'utm_campaign'];
  const missingRequiredUtms = requiredKeys.filter((key) => {
    const values = analysis.utmParams[key] ?? [];
    return values.filter((value) => value.trim().length > 0).length === 0;
  });
  const duplicateUtmKeys = analysis.duplicateKeys.filter((key) => utmKeySet.has(key));
  const extraTrackingParams = analysis.trackingParams.filter((entry) => !entry.isUtm);
  const hasAnyUtm = UTM_PARAM_KEYS.some((key) => (analysis.utmParams[key] ?? []).length > 0);
  const statusLabels: string[] = [];

  if (duplicateUtmKeys.length) {
    statusLabels.push('duplicate UTMs');
  }

  if (hasAnyUtm && missingRequiredUtms.includes('utm_campaign')) {
    statusLabels.push('missing campaign');
  } else if (hasAnyUtm && missingRequiredUtms.length) {
    statusLabels.push('missing required UTMs');
  }

  if (extraTrackingParams.length) {
    statusLabels.push('extra tracking params');
  }

  if (!statusLabels.length) {
    statusLabels.push(hasAnyUtm ? 'clean' : 'no campaign params');
  }

  return {
    destinationUrl: analysis.baseUrl,
    statusLabels,
    missingRequiredUtms,
    duplicateUtmKeys,
    extraTrackingParams,
    hasAnyUtm,
  };
}

export function slugifyAnchorFragment(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

export function buildAnchorLink(baseUrl: string, fragment: string) {
  const { normalized, url, warning } = normalizeUrlInput(baseUrl);
  const normalizedFragment = slugifyAnchorFragment(fragment.trim());

  if (!normalizedFragment) {
    throw new Error('Enter a fragment or heading first.');
  }

  url.hash = normalizedFragment;

  return {
    baseUrl: normalized,
    normalizedFragment,
    finalUrl: url.toString(),
    warning,
  };
}
